import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  ArrowLeft,
  Upload,
  Download,
  X,
  Image,
  Save
} from 'lucide-react'
import ProductEditor from '../components/ProductEditor'

const AdminProducts = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [uploading, setUploading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (!token || !userData) {
      navigate('/admin/login')
      return
    }

    loadProducts()
  }, [navigate])

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // 正确提取产品数组
          setProducts(data.data.products || [])
        }
      }
    } catch (error) {
      console.error('加载产品列表失败:', error)
      setProducts([]) // 出错时设置为空数组
    } finally {
      setLoading(false)
    }
  }

  // 发布产品
  const handlePublish = async (productId) => {
    if (!window.confirm('确定要发布这个产品吗？')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/products/${productId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          // 强制重新加载产品列表
          await loadProducts()
          setTimeout(() => {
            loadProducts()
          }, 500)
          alert('产品发布成功！')
        }
      } else {
        const errorData = await response.json()
        alert(`发布失败: ${errorData.message || '请重试'}`)
      }
    } catch (error) {
      console.error('发布产品失败:', error)
      alert('发布失败，请检查网络连接')
    }
  }

  // 撤销发布产品
  const handleUnpublish = async (productId) => {
    if (!window.confirm('确定要撤销发布这个产品吗？')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/products/${productId}/unpublish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          // 强制重新加载产品列表
          await loadProducts()
          setTimeout(() => {
            loadProducts()
          }, 500)
          alert('产品撤销发布成功！')
        }
      } else {
        const errorData = await response.json()
        alert(`撤销发布失败: ${errorData.message || '请重试'}`)
      }
    } catch (error) {
      console.error('撤销发布产品失败:', error)
      alert('撤销发布失败，请检查网络连接')
    }
  }

  const handleDelete = async (productId) => {
    if (!window.confirm('确定要删除这个产品吗？')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setProducts(products.filter(p => p.id !== productId))
      }
    } catch (error) {
      console.error('删除产品失败:', error)
    }
  }

  const handleOpenModal = async (product = null) => {
    if (product) {
      try {
        // 获取完整的产品详情（包含图片）
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/products/${product.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.ok) {
          const result = await response.json()
          if (result.success) {
            setEditingProduct(result.data)
          } else {
            setEditingProduct(product)
          }
        } else {
          setEditingProduct(product)
        }
      } catch (error) {
        console.error('获取产品详情失败:', error)
        setEditingProduct(product)
      }
    } else {
      setEditingProduct(null)
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingProduct(null)
  }

  const handleSaveProduct = async (formDataToSend, publish = false) => {
    setUploading(true)

    try {
      const token = localStorage.getItem('token')
      
      const url = editingProduct 
        ? `/api/products/${editingProduct.id}`
        : '/api/products'
      
      const method = editingProduct ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          // 强制重新加载产品列表，确保状态更新
          await loadProducts()
          
          // 添加一个小的延迟，确保状态更新完成
          setTimeout(() => {
            loadProducts()
          }, 500)
          
          handleCloseModal()
          alert(publish ? '产品发布成功！' : (editingProduct ? '产品更新成功！' : '产品保存为草稿成功！'))
        }
      } else {
        const errorData = await response.json()
        alert(`操作失败: ${errorData.message || '请重试'}`)
      }
    } catch (error) {
      console.error('保存产品失败:', error)
      alert('保存失败，请检查网络连接')
    } finally {
      setUploading(false)
    }
  }

  const handlePreviewProduct = (previewData) => {
    console.log('产品预览数据:', previewData)
    // 这里可以打开一个预览模态框或跳转到预览页面
    alert('预览功能已触发，请查看控制台输出预览数据')
  }

  // 从JSON数据中提取富文本内容 - 修复版，处理数组和其他数据类型
  const extractRichTextFromJson = (jsonData) => {
    if (!jsonData) return '';
    
    try {
      // 处理字符串类型
      if (typeof jsonData === 'string') {
        try {
          // 尝试解析JSON字符串
          const parsed = JSON.parse(jsonData);
          
          // 如果解析后是数组，转换为字符串表示
          if (Array.isArray(parsed)) {
            return parsed.join(', ');
          }
          
          // 如果解析后是对象
          if (parsed && typeof parsed === 'object') {
            // 如果是空对象，返回空字符串
            if (Object.keys(parsed).length === 0) {
              return '';
            }
            
            // 如果有content属性，返回content
            if (parsed.content !== undefined) {
              return String(parsed.content);
            }
            
            // 其他对象转换为JSON字符串
            return JSON.stringify(parsed);
          }
          
          // 其他类型直接转换为字符串
          return String(parsed);
        } catch {
          // 如果不是JSON格式，直接返回原字符串
          return jsonData;
        }
      }
      
      // 处理数组类型
      if (Array.isArray(jsonData)) {
        return jsonData.join(', ');
      }
      
      // 处理对象类型
      if (jsonData && typeof jsonData === 'object') {
        // 如果是空对象，返回空字符串
        if (Object.keys(jsonData).length === 0) {
          return '';
        }
        
        // 如果有content属性，返回content
        if (jsonData.content !== undefined) {
          return String(jsonData.content);
        }
        
        // 其他对象转换为JSON字符串
        try {
          return JSON.stringify(jsonData);
        } catch {
          return '';
        }
      }
      
      // 处理其他类型（数字、布尔值等）
      return String(jsonData);
    } catch (error) {
      console.error('提取富文本内容失败:', error, jsonData);
      return '';
    }
  }


  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <ArrowLeft size={16} />
                <span>返回仪表板</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                产品管理
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => handleOpenModal()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>添加产品</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 搜索和操作栏 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="搜索产品名称或描述..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex space-x-3">
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2">
                  <Upload size={16} />
                  <span>批量导入</span>
                </button>
                <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2">
                  <Download size={16} />
                  <span>导出数据</span>
                </button>
              </div>
            </div>
          </div>

          {/* 产品列表 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                产品列表 ({filteredProducts.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      产品信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      创建时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Package className="text-blue-600" size={20} />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {product.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {product.description?.substring(0, 50)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            product.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : product.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.status === 'published' ? '已发布' : 
                             product.status === 'draft' ? '草稿' : '未发布'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(product.createdAt).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleOpenModal(product)}
                              className="text-blue-600 hover:text-blue-900"
                              title="编辑"
                            >
                              <Edit size={16} />
                            </button>
                            {product.status === 'draft' && (
                              <button 
                                onClick={() => handlePublish(product.id)}
                                className="text-green-600 hover:text-green-900"
                                title="发布"
                              >
                                <Upload size={16} />
                              </button>
                            )}
                            {product.status === 'published' && (
                              <button 
                                onClick={() => handleUnpublish(product.id)}
                                className="text-orange-600 hover:text-orange-900"
                                title="撤销发布"
                              >
                                <Download size={16} />
                              </button>
                            )}
                            <button 
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600 hover:text-red-900"
                              title="删除"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center">
                        <Package className="mx-auto text-gray-400" size={48} />
                        <p className="mt-2 text-gray-500">暂无产品数据</p>
                        <button 
                          onClick={() => handleOpenModal()}
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                          添加第一个产品
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 添加/编辑产品模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingProduct ? '编辑产品' : '添加产品'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <ProductEditor
                initialData={editingProduct ? {
                  name: editingProduct.name || '',
                  description: editingProduct.description || '',
                  full_description: editingProduct.full_description || '',
                  price_info: extractRichTextFromJson(editingProduct.price_info) || '面议',
                  features: extractRichTextFromJson(editingProduct.features) || '',
                  specifications: extractRichTextFromJson(editingProduct.specifications) || '',
                  applications: extractRichTextFromJson(editingProduct.applications) || '',
                  support_info: extractRichTextFromJson(editingProduct.support_info) || '',
                  contact_image: editingProduct.contact_image || '',
                  status: editingProduct.status || 'draft',
                  images: editingProduct.images || [], // 传递图片数据
                  mainImageIndex: editingProduct.images ? editingProduct.images.findIndex(img => img.is_main) : 0
                } : null}
                onSave={handleSaveProduct}
                onCancel={handleCloseModal}
                isEditing={!!editingProduct}
                onPreview={handlePreviewProduct}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminProducts
